"use client"

import { sendGTMEvent } from "@next/third-parties/google"
import { ReducedProductData } from "@/lib/storefront/product/product"

type TrackingData = Record<string, any>

export type CustomerType = "b2b" | "b2c" | "guest"

export function toCustomerType(isAnonymous: boolean, isB2B: boolean): CustomerType {
  if (isAnonymous) return "guest"
  return isB2B ? "b2b" : "b2c"
}

export function getAffiliationFromHost(): string | undefined {
  if (typeof window === "undefined") return undefined
  return window.location.hostname
}

export function splitCategoryPath(categoryPath?: string) {
  const parts = (categoryPath ?? "").split("/").filter(Boolean)
  return {
    item_category: parts[0],
    item_category2: parts[1],
    item_category3: parts[2],
    item_category4: parts[3],
  }
}

function isEmptyTrackingValue(value: unknown) {
  return value === undefined || value === null || value === ""
}

export function removeEmptyTrackingValues<T>(value: T): T {
  if (Array.isArray(value)) {
    return value
      .map((item) => removeEmptyTrackingValues(item))
      .filter((item) => !isEmptyTrackingValue(item)) as T
  }

  if (value && typeof value === "object") {
    const cleaned = Object.entries(value as TrackingData).reduce<TrackingData>((acc, [key, entryValue]) => {
      const cleanedValue = removeEmptyTrackingValues(entryValue)

      if (isEmptyTrackingValue(cleanedValue)) {
        return acc
      }

      if (
        typeof cleanedValue === "object" &&
        !Array.isArray(cleanedValue) &&
        Object.keys(cleanedValue as TrackingData).length === 0
      ) {
        return acc
      }

      acc[key] = cleanedValue
      return acc
    }, {})

    return cleaned as T
  }

  return value
}

function isEcommerceReset(payload: unknown) {
  return (
    payload &&
    typeof payload === "object" &&
    !Array.isArray(payload) &&
    Object.keys(payload as TrackingData).length === 1 &&
    (payload as TrackingData).ecommerce === null
  )
}

export function buildGa4Item(params: {
  product: ReducedProductData
  priceGross?: number
  currency: string
  quantity?: number
  variant?: string
  categoryPath?: string
  index?: number
  item_list_id?: string
  item_list_name?: string
  affiliation?: string
}) {
  const { product, priceGross, currency, quantity, variant, categoryPath, index, item_list_id, item_list_name } = params
  const cats = splitCategoryPath(categoryPath)
  const base: any = {
    item_id: product.id,
    item_name: product.title,
    item_brand: product.brand,
    item_variant: variant,
    currency,
    quantity: quantity ?? 1,
    ...cats,
  }
  if (typeof priceGross === "number") base.price = priceGross
  if (typeof index === "number") base.index = index
  if (item_list_id) base.item_list_id = item_list_id
  if (item_list_name) base.item_list_name = item_list_name
  const affiliation = params.affiliation ?? getAffiliationFromHost()
  if (affiliation) base.affiliation = affiliation
  return removeEmptyTrackingValues(base)
}

export function resetEcommerce() {
  return { ecommerce: null as any }
}

// ===== Consent helpers (OneTrust) =====
export const ONETRUST_GROUPS = {
  strictlyNecessary: "C0001",
  analytics: "C0002",
  functional: "C0003",
  marketing: "C0004",
}

export function getOnetrustActiveGroups(): string {
  if (typeof window === "undefined") return ""
  const groups = (window as any).OnetrustActiveGroups
  return typeof groups === "string" ? groups : ""
}

export function isGroupActive(groupId: string): boolean {
  const groups = getOnetrustActiveGroups()
  // Groups format typically ",C0001,C0002,"
  return groups.includes(`,${groupId},`) || groups.endsWith(`,${groupId}`) || groups.startsWith(`${groupId},`)
}

export function isAnalyticsAllowed(): boolean {
  return isGroupActive(ONETRUST_GROUPS.analytics)
}

export function isMarketingAllowed(): boolean {
  return isGroupActive(ONETRUST_GROUPS.marketing)
}

export function sendGTMEventIfAnalyticsAllowed(payload: any) {
  if (!isAnalyticsAllowed()) {
    return false
  }
  sendGTMEvent(isEcommerceReset(payload) ? payload : removeEmptyTrackingValues(payload))
  return true
}
